import * as L from 'leaflet';
import { ccErrorCode, ccRequest } from '../../services/CCPublicationService';
import { requestQueue } from '../../services/CCMapGateway';
import { CC_V2_MIN_TILE_ZOOM, CC_V2_MAX_TILE_ZOOM, CC_V2_MAX_NATIVE_ZOOM } from './mapZoom';

// Shared only by CC raster layers. An old layer never closes the new layer's queue.
const queue = requestQueue(2);
const cancelled = () => Object.assign(new Error('Cancelled'), { code: 'ERR_CANCELED' });
const deadlineError = () => Object.assign(new Error('CC tile deadline exceeded'), { code: 'ETIMEDOUT' });
export function retryableTileError(error) {
  const code = ccErrorCode(error);
  if (code.startsWith('ci_') && !['ci_request_failed', 'ci_map_busy', 'ci_map_render_failed', 'ci_map_upstream_timeout'].includes(code)) return false;
  if (['ERR_CANCELED', 'ABORT_ERR'].includes(code)) return false;
  return [429, 502, 503, 504].includes(error.response?.status) ||
    (!error.response && ['ECONNABORTED', 'ETIMEDOUT'].includes(code));
}
function wait(delay, signal) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(cancelled()); return; }
    const abort = () => { clearTimeout(timer); reject(cancelled()); };
    const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, delay);
    signal.addEventListener('abort', abort, { once: true });
  });
}
function retryAfter(error) {
  const headers = error.response?.headers;
  const value = headers?.get?.('retry-after') ?? headers?.['retry-after'] ?? headers?.['Retry-After'];
  if (value == null || String(value).trim() === '') return 0;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : 0;
}
async function fetchTile(path, ownerSignal) {
  if (ownerSignal.aborted) throw cancelled();
  const controller = new AbortController();
  const abort = () => controller.abort();
  ownerSignal.addEventListener('abort', abort, { once: true });
  const deadline = Date.now() + 45000;
  const timer = setTimeout(abort, 45000);
  try {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      if (controller.signal.aborted || Date.now() >= deadline) throw deadlineError();
      try {
        const blob = await ccRequest(path, { signal: controller.signal, responseType: 'blob', timeout: Math.min(30000, deadline - Date.now()) });
        if (controller.signal.aborted || Date.now() >= deadline) throw deadlineError();
        return blob;
      } catch (error) {
        if (ownerSignal.aborted) throw cancelled();
        if (controller.signal.aborted || Date.now() >= deadline) throw deadlineError();
        if (!retryableTileError(error) || attempt === 4) throw error;
        const delay = Math.max(retryAfter(error), Math.min(8000, 1000 * (2 ** attempt) * (0.8 + Math.random() * 0.4)));
        await wait(Math.min(delay, deadline - Date.now()), controller.signal);
      }
    }
  } catch (error) {
    if (ownerSignal.aborted) throw cancelled();
    if (Date.now() >= deadline) throw deadlineError();
    throw error;
  } finally {
    clearTimeout(timer); ownerSignal.removeEventListener('abort', abort);
  }
}

// Lifecycle snapshots describe visible tiles, not whichever request finished last.
export function authenticatedTiles(scope, onError, onStatus = () => {}) {
  const pending = new Map();
  // Leaflet recreates tiles during non-animated zooms, even with maxNativeZoom.
  // Reuse only this authorized layer's recent blobs, with bounded memory.
  const cache = new Map(); let cacheBytes = 0;
  let layer; let removed = false;
  const visible = (state) => {
    if (!layer?._map) return true;
    const zoom = layer._map.getZoom();
    if (zoom < CC_V2_MIN_TILE_ZOOM || zoom > CC_V2_MAX_TILE_ZOOM) return false;
    const native = Math.min(Math.round(zoom), CC_V2_MAX_NATIVE_ZOOM);
    if (state.coords.z !== native) return false;
    const center = layer._map.project(layer._map.getCenter(), native);
    const halfSize = layer._map.getSize().divideBy(2 * layer._map.getZoomScale(zoom, native));
    const bounds = L.bounds(center.subtract(halfSize), center.add(halfSize));
    return bounds.intersects(L.bounds([state.coords.x * 256, state.coords.y * 256], [(state.coords.x + 1) * 256, (state.coords.y + 1) * 256]));
  };
  const report = () => {
    if (removed) return;
    const states = [...pending.values()].filter(visible);
    onStatus({ pending: states.filter((s) => s.status === 'pending').length,
      errors: states.filter((s) => s.status === 'error').map((s) => s.error),
      successful: states.filter((s) => s.status === 'ready').length });
  };
  const release = (tile) => {
    const state = pending.get(tile);
    if (!state) return;
    state.controller.abort();
    tile.onload = null; tile.onerror = null;
    if (state.url) URL.revokeObjectURL(state.url);
    tile.removeAttribute('src'); pending.delete(tile); report();
  };
  const load = (tile, state) => {
    state.controller.abort(); state.controller = new AbortController();
    const { signal } = state.controller;
    state.status = 'pending'; state.error = null; report();
    const settle = (error) => {
      if (signal.aborted || removed) return;
      tile.onload = null; tile.onerror = null;
      if (error && cache.has(key)) { cacheBytes -= cache.get(key).blob.size; cache.delete(key); }
      if (error && state.url) { URL.revokeObjectURL(state.url); state.url = null; tile.removeAttribute('src'); }
      state.status = error ? 'error' : 'ready'; state.error = error; report();
      if (!error) L.DomUtil.addClass(tile, 'leaflet-tile-loaded');
      // Leaflet completion is once per live tile, even after a manual recovery.
      if (!state.done) { state.done = true; state.complete(error || null, tile); }
      if (error) onError(error);
    };
    const { coords } = state;
    const key = `${coords.z}/${coords.x}/${coords.y}`;
    queue.run(() => {
      const hit = cache.get(key);
      if (hit && Date.now() - hit.time < 60000) return hit.blob;
      return fetchTile(scope.tile_path ? scope.tile_path(coords) : `map-scopes/${encodeURIComponent(scope.map_scope_id)}/tiles/${key}.png`, signal);
    }, signal)
      .then((blob) => {
        if (signal.aborted || removed) return;
        if (!(blob instanceof Blob) || !blob.type.startsWith('image/png')) throw new Error('Invalid CC tile image');
        const cached = cache.get(key);
        if (cached) { cacheBytes -= cached.blob.size; cache.delete(key); }
        if (blob.size <= 32 * 1024 * 1024) {
          while (cache.size >= 64 || cacheBytes + blob.size > 32 * 1024 * 1024) {
            const oldest = cache.keys().next().value; cacheBytes -= cache.get(oldest).blob.size; cache.delete(oldest);
          }
          cache.set(key, { blob, time: cached?.blob === blob ? cached.time : Date.now() }); cacheBytes += blob.size;
        }
        if (state.url) URL.revokeObjectURL(state.url);
        state.url = URL.createObjectURL(blob);
        tile.onload = () => settle(); tile.onerror = () => settle(new Error('CC tile decode failed'));
        tile.src = state.url;
      }).catch(settle);
  };
  const Layer = L.GridLayer.extend({
    createTile(coords, done) {
      const tile = document.createElement('img'); tile.alt = ''; tile.setAttribute('role', 'presentation');
      const state = { coords, controller: new AbortController(), complete: done, done: false, url: null };
      pending.set(tile, state); load(tile, state); return tile;
    },
    retryFailed() { pending.forEach((state, tile) => { if (state.status === 'error' && visible(state)) load(tile, state); }); },
    onAdd(map) { removed = false; L.GridLayer.prototype.onAdd.call(this, map); map.on('moveend zoomend', report); },
    onRemove(map) {
      removed = true; map.off?.('moveend zoomend', report);
      [...pending.keys()].forEach(release);
      cache.clear(); cacheBytes = 0;
      L.GridLayer.prototype.onRemove.call(this, map);
    },
  });
  layer = new Layer({ minZoom: CC_V2_MIN_TILE_ZOOM, maxZoom: CC_V2_MAX_TILE_ZOOM, maxNativeZoom: CC_V2_MAX_NATIVE_ZOOM,
    tileSize: 256, zIndex: 360, keepBuffer: 0, updateWhenIdle: true, noWrap: true });
  layer.on('tileunload', ({ tile }) => release(tile));
  return layer;
}
